/* eslint-disable @typescript-eslint/no-unused-vars */
import {
  ArrowBackOutlined,
  BusinessOutlined,
  InventoryOutlined,
  LocalShippingOutlined,
} from "@mui/icons-material";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Divider,
  Grid,
  Paper,
  Snackbar,
  TextField,
  Typography,
  alpha,
} from "@mui/material";
import { useCallback, useEffect, useRef, useState } from "react";
import { GetAllVendorsListApiResponse } from "../../../@types/DRO/GetAllVendorsResponse";
import { GetAllWarehouseApiResponse } from "../../../@types/DRO/GetAllWarehouseResponse";
import {
  GetAllModelTypeArray,
  TransferOrderPartDetails,
} from "../../../@types/DRO/GetPartsListResponse";
import { AllPartApi } from "../../../api/GetParts/GetAllPartsApi";
import { GetAllVendorsListApi } from "../../../api/GetVendorsList/GetAllVendorsListApi";
import { GetAllWarehouseListApi } from "../../../api/GetWarehouseList/GetAllWarehouseListApi";
import { ProductOrderApi } from "../../../api/ProductOrder/ProductOrderApi";
import { useLoading } from "../../../views/Loader/LoadingContext";
import CreateDeliveryOrder from "./CreateDeliveryOrder";

interface NewOrderProp {
  handleBackToOrders: () => void;
}

const NewDeliveryOrderForm: React.FC<NewOrderProp> = ({
  handleBackToOrders,
}) => {
  const [page, setPage] = useState(0);
  const [rowsPerPage] = useState(10); // Fixed page size
  const [searchQuery, setSearchQuery] = useState("");
  const [parts, setParts] = useState<TransferOrderPartDetails[]>([]);
  const [selectedOEMTypeId, setSelectedOEMTypeId] = useState<number | null>(
    null
  );
  const [modelTypes, setModelTypes] = useState<GetAllModelTypeArray[]>([]);
  const [vendors, setVendors] = useState<
    GetAllVendorsListApiResponse["data"]["data"]
  >([]);
  const [warehouses, setWarehouses] = useState<
    GetAllWarehouseApiResponse["data"]["data"]
  >([]);
  const [selectedVendor, setSelectedVendor] = useState("");
  const [vendorId, setVendorId] = useState<number | null>(null);
  const [warehouseId, setWarehouseId] = useState<number | null>(null);
  const [partId, setPartId] = useState<number | null>(null);
  const [orderId, setOrderId] = useState<number | null>(null);
  const [selectedWarehouse, setSelectedWarehouse] = useState("");
  const [selectedPart, setSelectedPart] =
    useState<TransferOrderPartDetails | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasMoreVendors, setHasMoreVendors] = useState(true);
  const [hasMoreParts, setHasMoreParts] = useState(true);
  const [hasMoreWarehouses, setHasMoreWarehouses] = useState(true);
  const { setLoading } = useLoading();
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success" as "success" | "error" | "info" | "warning",
  });

  // Refs for autocomplete components
  const vendorAutocompleteRef = useRef<any>(null);
  const warehouseAutocompleteRef = useRef<any>(null);
  const partAutocompleteRef = useRef<any>(null);

  const showSnackbar = (
    message: string,
    severity: "success" | "error" | "info" | "warning"
  ) => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };
  // Debounce function to limit API calls during search
  const debounce = (func: Function, delay: number) => {
    let timer: NodeJS.Timeout;
    return (...args: any[]) => {
      clearTimeout(timer);
      timer = setTimeout(() => func(...args), delay);
    };
  };

  const fetchModelTypes = useCallback(async () => {
    try {
      const response = await AllPartApi.api.getModelTypeAll();
      setModelTypes(response.data);
    } catch (error) {
      console.error("Error fetching model types", error);
    }
  }, []);

  const fetchVendors = useCallback(
    async (pageNumber: number, search: string, reset: boolean = false) => {
      try {
        const response = await GetAllVendorsListApi.api.getAllVendorsList(
          pageNumber,
          rowsPerPage,
          search
        );

        if (reset) {
          setVendors(response.data.data);
          setHasMoreVendors(response.data.data.length === rowsPerPage);
        } else {
          setVendors((prev) => [...prev, ...response.data.data]);
          setHasMoreVendors(response.data.data.length === rowsPerPage);
        }
      } catch (error) {
        console.error("Error fetching vendors", error);
      }
    },
    [rowsPerPage]
  );

  const fetchParts = useCallback(
    async (pageNumber: number, search: string, reset: boolean = false) => {
      try {
        const response = await AllPartApi.api.getPartList(
          pageNumber,
          rowsPerPage,
          search
        );

        if (reset) {
          setParts(response.data.data);
          setHasMoreParts(response.data.data.length === rowsPerPage);
        } else {
          setParts((prev) => [...prev, ...response.data.data]);
          setHasMoreParts(response.data.data.length === rowsPerPage);
        }
      } catch (error) {
        console.error("Error fetching parts", error);
      }
    },
    [rowsPerPage]
  );

  const fetchWarehouses = useCallback(
    async (pageNumber: number, search: string, reset: boolean = false) => {
      try {
        const response = await GetAllWarehouseListApi.api.getAllWarehouseList(
          pageNumber,
          rowsPerPage,
          search,
          true
        );

        if (reset) {
          setWarehouses(response.data.data);
          setHasMoreWarehouses(response.data.data.length === rowsPerPage);
        } else {
          setWarehouses((prev) => [...prev, ...response.data.data]);
          setHasMoreWarehouses(response.data.data.length === rowsPerPage);
        }
      } catch (error) {
        console.error("Error fetching warehouses", error);
      }
    },
    [rowsPerPage]
  );

  // Debounced search functions
  const debouncedVendorSearch = useCallback(
    debounce((searchValue: string) => {
      setPage(0);
      fetchVendors(0, searchValue, true);
    }, 500),
    [fetchVendors]
  );

  const debouncedPartSearch = useCallback(
    debounce((searchValue: string) => {
      setPage(0);
      fetchParts(0, searchValue, true);
    }, 500),
    [fetchParts]
  );

  const debouncedWarehouseSearch = useCallback(
    debounce((searchValue: string) => {
      setPage(0);
      fetchWarehouses(0, searchValue, true);
    }, 500),
    [fetchWarehouses]
  );

  // Initial data fetch
  useEffect(() => {
    fetchModelTypes();
    fetchVendors(0, "", true);
    fetchParts(0, "", true);
    fetchWarehouses(0, "", true);
  }, [fetchModelTypes, fetchVendors, fetchParts, fetchWarehouses]);

  // Handle scroll for infinite loading
  const handleVendorScroll = (event: React.SyntheticEvent) => {
    const listboxNode = event.currentTarget;
    const position = listboxNode.scrollTop + listboxNode.clientHeight;
    const threshold = listboxNode.scrollHeight - 10;

    if (position >= threshold && hasMoreVendors) {
      const nextPage = Math.floor(vendors.length / rowsPerPage);
      fetchVendors(nextPage, searchQuery);
    }
  };

  const handlePartScroll = (event: React.SyntheticEvent) => {
    const listboxNode = event.currentTarget;
    const position = listboxNode.scrollTop + listboxNode.clientHeight;
    const threshold = listboxNode.scrollHeight - 10;

    if (position >= threshold && hasMoreParts) {
      const nextPage = Math.floor(parts.length / rowsPerPage);
      fetchParts(nextPage, searchQuery);
    }
  };

  const handleWarehouseScroll = (event: React.SyntheticEvent) => {
    const listboxNode = event.currentTarget;
    const position = listboxNode.scrollTop + listboxNode.clientHeight;
    const threshold = listboxNode.scrollHeight - 10;

    if (position >= threshold && hasMoreWarehouses) {
      const nextPage = Math.floor(warehouses.length / rowsPerPage);
      fetchWarehouses(nextPage, searchQuery);
    }
  };

  const handleSubmit = async () => {
    setLoading(true);
    if (!vendorId || !warehouseId || !partId) {
      showSnackbar("Please fill all the required fields", "error");
      setLoading(false);
      return;
    }

    setIsSubmitting(true);
    try {
      const response =
        await ProductOrderApi.api.getSingleProductOrderDetailsByCriteria(
          partId,
          warehouseId,
          vendorId
        );

      if (response?.data?.id) {
        setOrderId(response.data.id);
        showSnackbar(
          response?.message || "Order created successfully!",
          "success"
        );
      } else {
        showSnackbar(
          response?.message || "Failed to create order. Please try again.",
          "error"
        );
      }
    } catch (error: any) {
      console.error("Error creating order", error);
      showSnackbar(
        error.response?.data?.message ||
          error.message ||
          "Error creating order. Please try again.",
        "error"
      );
    } finally {
      setLoading(false);
      setIsSubmitting(false);
    }
  };

  if (orderId) {
    return (
      <CreateDeliveryOrder orderId={orderId} onBack={() => setOrderId(null)} />
    );
  }

  return (
    <Box sx={{ bgcolor: "#f5f5f5", minHeight: "100vh", p: 3 }}>
      <Paper
        elevation={0}
        sx={{
          borderRadius: 3,
          overflow: "hidden",
          bgcolor: "white",
          border: "1px solid",
          borderColor: "divider",
        }}>
        {/* Header */}
        <Box
          sx={{
            p: 3,
            bgcolor: "primary.main",
            background: "linear-gradient(135deg, #1976d2 0%, #1565c0 100%)",
            color: "white",
          }}>
          <Typography variant="h5" fontWeight={600}>
            Create New Delivery Order
          </Typography>
          <Typography variant="body2" sx={{ mt: 0.5, opacity: 0.9 }}>
            Fill in the details below to create a new delivery order
          </Typography>
        </Box>

        {/* Form Content */}
        <Box sx={{ p: 4 }}>
          <Grid container spacing={4}>
            {/* Vendor Selection Card */}
            <Grid item xs={12} md={4}>
              <Card
                elevation={0}
                sx={{
                  height: "100%",
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 2,
                  transition: "all 0.3s",
                  "&:hover": {
                    borderColor: "primary.main",
                    boxShadow: (theme) =>
                      `0 0 0 1px ${alpha(theme.palette.primary.main, 0.1)}`,
                  },
                }}>
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                    <BusinessOutlined sx={{ mr: 1, color: "primary.main" }} />
                    <Typography variant="h6" fontWeight={500}>
                      Vendor
                    </Typography>
                  </Box>
                  <Autocomplete
                    ref={vendorAutocompleteRef}
                    fullWidth
                    options={vendors}
                    getOptionLabel={(option) => option.name}
                    value={
                      vendors.find((vendor) => vendor.id === vendorId) || null
                    }
                    onChange={(event, newValue) => {
                      setSelectedVendor(newValue ? newValue.name : "");
                      setVendorId(newValue?.id ?? null);
                    }}
                    onInputChange={(event, newInputValue) => {
                      debouncedVendorSearch(newInputValue);
                    }}
                    ListboxProps={{
                      onScroll: handleVendorScroll,
                      style: { maxHeight: 200, overflow: "auto" },
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        variant="outlined"
                        placeholder="Search and select vendor"
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            bgcolor: "grey.50",
                          },
                        }}
                      />
                    )}
                  />
                </CardContent>
              </Card>
            </Grid>

            {/* Warehouse Selection Card */}
            <Grid item xs={12} md={4}>
              <Card
                elevation={0}
                sx={{
                  height: "100%",
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 2,
                  transition: "all 0.3s",
                  "&:hover": {
                    borderColor: "primary.main",
                    boxShadow: (theme) =>
                      `0 0 0 1px ${alpha(theme.palette.primary.main, 0.1)}`,
                  },
                }}>
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                    <LocalShippingOutlined
                      sx={{ mr: 1, color: "primary.main" }}
                    />
                    <Typography variant="h6" fontWeight={500}>
                      Deliver To
                    </Typography>
                  </Box>
                  <Autocomplete
                    ref={warehouseAutocompleteRef}
                    fullWidth
                    options={warehouses}
                    getOptionLabel={(option) => option.name}
                    value={
                      warehouses.find(
                        (warehouse) => warehouse.id === warehouseId
                      ) || null
                    }
                    onChange={(event, newValue) => {
                      setSelectedWarehouse(newValue ? newValue.name : "");
                      setWarehouseId(newValue?.id ?? null);
                    }}
                    onInputChange={(event, newInputValue) => {
                      debouncedWarehouseSearch(newInputValue);
                    }}
                    ListboxProps={{
                      onScroll: handleWarehouseScroll,
                      style: { maxHeight: 200, overflow: "auto" },
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        variant="outlined"
                        placeholder="Search and select warehouse"
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            bgcolor: "grey.50",
                          },
                        }}
                      />
                    )}
                  />
                </CardContent>
              </Card>
            </Grid>

            {/* Part Selection Card */}
            <Grid item xs={12} md={4}>
              <Card
                elevation={0}
                sx={{
                  height: "100%",
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 2,
                  transition: "all 0.3s",
                  "&:hover": {
                    borderColor: "primary.main",
                    boxShadow: (theme) =>
                      `0 0 0 1px ${alpha(theme.palette.primary.main, 0.1)}`,
                  },
                }}>
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                    <InventoryOutlined sx={{ mr: 1, color: "primary.main" }} />
                    <Typography variant="h6" fontWeight={500}>
                      Part Type
                    </Typography>
                  </Box>
                  <Autocomplete
                    ref={partAutocompleteRef}
                    fullWidth
                    options={parts}
                    getOptionLabel={(option) => option.description}
                    value={parts.find((part) => part.id === partId) || null}
                    onChange={(event, newValue) => {
                      setPartId(newValue ? newValue.id : null);
                      setSelectedPart(newValue);
                    }}
                    onInputChange={(event, newInputValue) => {
                      debouncedPartSearch(newInputValue);
                    }}
                    ListboxProps={{
                      onScroll: handlePartScroll,
                      style: { maxHeight: 200, overflow: "auto" },
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        variant="outlined"
                        placeholder="Search and select part type"
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            bgcolor: "grey.50",
                          },
                        }}
                      />
                    )}
                  />
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Action Buttons */}
          <Divider sx={{ my: 4 }} />

          <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2 }}>
            <Button
              variant="outlined"
              onClick={handleBackToOrders}
              startIcon={<ArrowBackOutlined />}
              sx={{
                px: 3,
                py: 1.2,
                borderRadius: 2,
                textTransform: "none",
                fontSize: "0.95rem",
                fontWeight: 500,
              }}>
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleSubmit}
              disabled={isSubmitting || !vendorId || !warehouseId || !partId}
              sx={{
                px: 4,
                py: 1.2,
                borderRadius: 2,
                textTransform: "none",
                fontSize: "0.95rem",
                fontWeight: 500,
                bgcolor: "primary.main",
                "&:hover": {
                  bgcolor: "primary.dark",
                },
                "&:disabled": {
                  bgcolor: "grey.300",
                },
              }}>
              {isSubmitting ? "Finding Order..." : "Find PO"}
            </Button>
          </Box>
        </Box>
      </Paper>

      {/* Snackbar */}
      <Snackbar
        key={snackbar.message}
        open={snackbar.open}
        autoHideDuration={snackbar.severity === "error" ? 3000 : 2000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}>
        <Alert
          elevation={6}
          variant="filled"
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{
            width: "100%",
            alignItems: "center",
          }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default NewDeliveryOrderForm;
